import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  output,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  ClienteService,
  EstabelecimentoDataService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { I18nService } from '@nomoh-monorepo/data-access-shared-i18n';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  DeviceService,
  FormFieldComponent,
  NFieldsetComponent,
  NMultiselectComponent,
  PhoneInputComponent,
} from '@nomoh-monorepo/shared-common-ui';
import { IEstabelecimentoClienteEntity } from '@nomoh-monorepo/shared-types';
import { MaskUtils } from '@nomoh-monorepo/shared-util-tools';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

@Component({
  selector: 'lib-cliente-create-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    DatePickerModule,
    SelectModule,
    TextareaModule,
    ToggleSwitchModule,
    FormFieldComponent,
    NFieldsetComponent,
    NMultiselectComponent,
    PhoneInputComponent,
    TranslateModule,
  ],
  templateUrl: './cliente-create-edit.component.html',
  styleUrls: ['./cliente-create-edit.component.scss'],
})
export class ClienteCreateEditComponent
  implements OnInit, AfterViewInit, OnChanges {
  closed = output<void>();
  saved = output<void>();
  @Input() cliente: IEstabelecimentoClienteEntity | null = null;

  @ViewChild('footerTemplate', { static: false })
  private _footerTemplateRef!: TemplateRef<unknown>;

  @Output() footerTemplateAction = new EventEmitter<TemplateRef<unknown>>();

  private fb = inject(FormBuilder);
  private clienteService = inject(ClienteService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private notificacaoService = inject(NotificacaoService);
  private translateService = inject(TranslateService);
  private i18nService = inject(I18nService);
  private langChangeTrigger = toSignal(this.translateService.onLangChange);
  deviceService = inject(DeviceService);
  $nicho = inject(NichoTermosService).$nicho;


  $dropdownAppendTo = computed(() =>
    this.deviceService.$isMobile() ? undefined : 'body'
  );

  $estabelecimentoAtual = this.estabelecimentoDataService.$estabelecimentoAtual;
  salvando = signal(false);

  $sexoOpcoes = computed(() => {
    this.langChangeTrigger();
    this.i18nService.idioma();
    return [
      { label: this.translateService.instant('BARDO_CLIENTES.NAO_INFORMADO') || 'Não informado', value: 0 },
      { label: this.translateService.instant('BARDO_CLIENTES.MASCULINO') || 'Masculino', value: 1 },
      { label: this.translateService.instant('BARDO_CLIENTES.FEMININO') || 'Feminino', value: 2 },
    ];
  });

  get sexoOpcoes() {
    return this.$sexoOpcoes();
  }

  dataMaximaNascimento = new Date();

  get dataMaximaNascimentoIso(): string {
    const d = this.dataMaximaNascimento;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private parseDateLocal(val: Date | string | null | undefined): Date | null {
    if (!val) return null;
    if (val instanceof Date) return val;
    const str = String(val).trim();
    const apenasData = str.split('T')[0];
    const partes = apenasData.split('-');
    if (partes.length === 3) {
      const ano = parseInt(partes[0], 10);
      const mes = parseInt(partes[1], 10) - 1;
      const dia = parseInt(partes[2], 10);
      if (!isNaN(ano) && !isNaN(mes) && !isNaN(dia)) {
        return new Date(ano, mes, dia);
      }
    }
    return new Date(val);
  }

  private dateToIso(dateVal: Date | string): string {
    if (typeof dateVal === 'string') {
      return dateVal.split('T')[0];
    }
    const d = this.parseDateLocal(dateVal) || new Date(dateVal);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(d.getDate()).padStart(2, '0')}`;
  }

  aplicarMascaraData(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input?.value) return;
    const formatado = MaskUtils.formatarData(input.value);
    if (input.value !== formatado) {
      input.value = formatado;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  form: FormGroup = this.fb.group({
    nome: [
      '',
      [Validators.required, Validators.minLength(3), Validators.maxLength(100)],
    ],
    telefone: ['', [Validators.required]],
    email: ['', [Validators.email]],
    dataNascimento: [null, [Validators.required]],
    sexo: [null],
    endereco: ['', [Validators.maxLength(500)]],
    observacao: [''],
    cadastrarDependente: [false],
    nomeDependente: [''],
    dataNascimentoDependente: [null],
    sexoDependente: [null],
  });

  ngOnInit(): void {
    this.form.get('cadastrarDependente')?.valueChanges.subscribe((enabled) => {
      this.onCadastrarDependenteChange(!!enabled);
    });
  }

  onCadastrarDependenteChange(enabled: boolean): void {
    const nomeDepCtrl = this.form.get('nomeDependente');
    const dataNascDepCtrl = this.form.get('dataNascimentoDependente');

    if (enabled) {
      nomeDepCtrl?.setValidators([
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
      ]);
      dataNascDepCtrl?.setValidators([Validators.required]);
    } else {
      nomeDepCtrl?.clearValidators();
      dataNascDepCtrl?.clearValidators();
      nomeDepCtrl?.setValue('');
      dataNascDepCtrl?.setValue(null);
      this.form.get('sexoDependente')?.setValue(null);
    }
    nomeDepCtrl?.updateValueAndValidity();
    dataNascDepCtrl?.updateValueAndValidity();
  }

  ngAfterViewInit(): void {
    this.footerTemplateAction.emit(this._footerTemplateRef);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['cliente']) {
      this.prepararFormulario();
    }
  }

  private prepararFormulario() {
    if (this.cliente?.id) {
      const dataNascLocal = this.parseDateLocal(this.cliente.dataNascimento);
      this.form.patchValue({
        nome: this.cliente.nome,
        telefone: this.cliente.telefone || '',
        email: this.cliente.email || '',
        dataNascimento: dataNascLocal
          ? this.deviceService.$isMobile()
            ? this.dateToIso(dataNascLocal)
            : dataNascLocal
          : null,
        sexo: this.cliente.sexo ?? null,
        endereco: this.cliente.endereco || '',
        observacao: this.cliente.observacao || '',
        cadastrarDependente: false,
        nomeDependente: '',
        dataNascimentoDependente: null,
        sexoDependente: null,
      });
      this.onCadastrarDependenteChange(false);
      return;
    }
    this.form.reset();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.onCadastrarDependenteChange(false);
  }

  onTelefoneChange(value: string): void {
    const ctrl = this.form.get('telefone');
    ctrl?.setValue(value);
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
  }

  async salvar() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const estabelecimentoId = this.$estabelecimentoAtual()?.id;
    if (!estabelecimentoId) {
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('BARDO_CLIENTES.NOTIFICACOES.ESTABELECIMENTO_NAO_ENCONTRADO')
      );
      return;
    }

    this.salvando.set(true);
    try {
      const formVal = this.form.value;
      const telefoneLimpo = formVal.telefone || undefined;

      const dataNascFinal = formVal.dataNascimento
        ? this.dateToIso(formVal.dataNascimento)
        : undefined;

      const dados: Partial<IEstabelecimentoClienteEntity> = {
        nome: formVal.nome,
        telefone: telefoneLimpo || undefined,
        email: formVal.email || undefined,
        dataNascimento: dataNascFinal,
        sexo: formVal.sexo ?? undefined,
        endereco: formVal.endereco || undefined,
        observacao: formVal.observacao || undefined,
        estabelecimentoId,
      };

      if (this.cliente?.id) {
        await this.clienteService.atualizar({ ...dados, id: this.cliente.id });
        this.notificacaoService.notificacaoSuccess(
          this.translateService.instant('BARDO_CLIENTES.NOTIFICACOES.CLIENTE_ATUALIZADO')
        );
      } else {
        const responsavelId = await this.clienteService.criar(dados);

        if (formVal.cadastrarDependente && formVal.nomeDependente?.trim()) {
          const dadosDependente: Partial<IEstabelecimentoClienteEntity> = {
            nome: formVal.nomeDependente.trim(),
            dataNascimento: formVal.dataNascimentoDependente
              ? this.dateToIso(formVal.dataNascimentoDependente)
              : undefined,
            estabelecimentoId,
          };

          const dependenteId = await this.clienteService.criar(dadosDependente);

          await this.clienteService.criarVinculo({
            estabelecimentoId,
            responsavelId,
            clienteId: dependenteId,
            contatoPrincipal: true,
          });

          this.notificacaoService.notificacaoSuccess(
            this.translateService.instant('BARDO_CLIENTES.NOTIFICACOES.CLIENTE_E_DEPENDENTE_CADASTRADOS')
          );
        } else {
          this.notificacaoService.notificacaoSuccess(
            this.translateService.instant('BARDO_CLIENTES.NOTIFICACOES.CLIENTE_CADASTRADO')
          );
        }

        this.form.reset();
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.onCadastrarDependenteChange(false);
      }

      this.saved.emit();
    } catch (error) {
      console.error('Erro ao salvar cliente/dependente:', error);
    } finally {
      this.salvando.set(false);
    }
  }
}
